<template>
  <div class="col-12 h-100 p-0">
    <div class="wm" :class="{'wm-sidebar-collapsed': sidebar_collapsed}">

      <aside class="wm-sidebar">
        <div class="wm-sidebar-top">
          <button class="wm-icon-btn" type="button" title="Menu" @click="sidebar_collapsed = !sidebar_collapsed">
            <i class="fas fa-bars"></i>
          </button>
        </div>

        <button class="wm-compose-btn" type="button" title="New Message" @click="openComposePopup">
          <i class="fas fa-pen"></i>
          <span class="wm-hide-collapsed">New Message</span>
        </button>

        <div class="wm-account wm-hide-collapsed" v-if="emailAccounts.length">
          <select class="wm-account-select" v-model="selectedEmailAccount" @change="updateActiveAccount">
            <option v-for="account in emailAccounts" :key="account.id" :value="account.id">
              {{ account.email }}
            </option>
          </select>
        </div>

        <nav class="wm-folders">
          <template v-if="sortedFolders.length">
            <router-link v-for="folder in sortedFolders" :key="folder.id"
                         :to="'/0/' + getFolderType(folder)" class="wm-folder" :title="folder.name"
                         :style="sidebar_collapsed ? {} : { paddingLeft: (20 + folder.depth * 12) + 'px' }">
              <i :class="getFolderIcon(folder)"></i>
              <span class="wm-folder-name wm-hide-collapsed">{{ folder.name }}</span>
              <span v-if="getFolderUnread(folder)" class="wm-folder-count">{{ getFolderUnread(folder) }}</span>
            </router-link>
          </template>

          <template v-else>
            <router-link to="/0/inbox" class="wm-folder" title="Inbox">
              <i class="fas fa-inbox"></i>
              <span class="wm-folder-name wm-hide-collapsed">Inbox</span>
              <span v-if="unread_inbox_count" class="wm-folder-count">{{ unread_inbox_count }}</span>
            </router-link>
            <router-link to="/0/sent" class="wm-folder" title="Sent Items">
              <i class="far fa-paper-plane"></i>
              <span class="wm-folder-name wm-hide-collapsed">Sent Items</span>
            </router-link>
            <router-link to="/0/trash" class="wm-folder" title="Deleted">
              <i class="far fa-trash-alt"></i>
              <span class="wm-folder-name wm-hide-collapsed">Deleted</span>
            </router-link>
          </template>
        </nav>

        <div class="wm-sync wm-hide-collapsed" v-if="syncing">
          <div class="wm-sync-label">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            Syncing... {{ sync_percentage }}%
          </div>
          <div class="progress">
            <div class="progress-bar" role="progressbar" :style="syncProgress" :aria-valuenow="sync_percentage"
                 aria-valuemin="0" aria-valuemax="100"></div>
          </div>
        </div>
      </aside>

      <section class="wm-list">
        <div class="wm-list-toolbar">
          <div class="wm-search">
            <i class="fas fa-search"></i>
            <input type="text"
                   placeholder="Search mail"
                   v-on:keyup="onSearchChange"
                   v-on:keyup.enter="triggerSearch"
                   v-on:keyup.esc="clearSearch"
                   v-model="search">
            <button v-if="search.length" class="wm-icon-btn wm-icon-btn-sm" type="button" title="Clear search"
                    @click="clearSearch">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="wm-list-actions" v-if="type !== 'trash'">
            <button class="wm-icon-btn wm-icon-btn-sm" type="button" title="Select all" @click="selectAllThreads">
              <i :class="allThreadsSelected ? 'fas fa-check-square' : 'far fa-square'"></i>
            </button>
            <template v-if="selected_threads.length">
              <button class="wm-icon-btn wm-icon-btn-sm" type="button" title="Delete" @click="deleteThreads">
                <i class="far fa-trash-alt"></i>
              </button>
              <span class="wm-selected-count">{{ selected_threads.length }} selected</span>
            </template>
          </div>
        </div>

        <div class="wm-threads incoming-emails">
          <div class="wm-thread"
               v-for="thread in threads" :key="thread"
               :class="{
                 'active': thread === active_thread_id,
                 'unread': !isThreadRead(thread),
                 'selected': isThreadMarked(thread)
               }">
            <button v-if="type !== 'trash'" class="wm-thread-check" type="button" title="Select"
                    @click="toggleThread(thread, $event)">
              <i :class="isThreadMarked(thread) ? 'fas fa-check-square' : 'far fa-square'"></i>
            </button>

            <router-link class="wm-thread-link" :to="{name : 'inbox_thread', params : {type : type, thread : thread}}">
              <div class="wm-thread-top">
                <span class="wm-thread-from" v-html="getThreadUser(thread)"></span>
                <span v-if="getThreadMessagesCount(thread)" class="wm-thread-count">
                  {{ getThreadMessagesCount(thread) }}
                </span>
                <span class="wm-thread-date">{{ getFormattedDate(thread) }}</span>
              </div>
              <div class="wm-thread-subject" v-html="getThreadSubject(thread)"></div>
              <div class="wm-thread-snippet" v-html="getThreadSnippet(thread)"></div>
            </router-link>

            <button class="wm-icon-btn wm-icon-btn-sm wm-thread-read-toggle" type="button"
                    :title="isThreadRead(thread) ? 'Mark as unread' : 'Mark as read'"
                    @click="toggleThreadRead(thread, $event)">
              <i :class="isThreadRead(thread) ? 'far fa-envelope' : 'far fa-envelope-open'"></i>
            </button>
          </div>

          <div v-if="loading" class="wm-list-state">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </div>
          <div v-else-if="!threads.length" class="wm-list-state">
            {{ search.length ? 'No messages match your search' : 'No messages' }}
          </div>
        </div>
      </section>

      <section class="wm-reader active-email">
        <template v-if="active_thread">
          <div class="wm-reader-header">
            <h2 class="wm-reader-subject">{{ getActiveThreadSubject() }}</h2>
            <span v-if="active_thread.length > 1" class="wm-reader-count">{{ active_thread.length }} messages</span>
          </div>

          <article class="wm-message" v-for="message in active_thread" :key="message.id">
            <div class="wm-message-head">
              <div class="wm-avatar" :style="{backgroundColor: getAvatarColor(getMessageSender(message))}">
                {{ getInitial(getMessageSender(message)) }}
              </div>

              <div class="wm-message-meta">
                <div class="wm-message-from">
                  <span class="wm-message-name">{{ getDisplayName(getMessageSender(message)) }}</span>
                  <span class="wm-message-email" v-if="message.type === 'INBOX' && message.received_from">
                    &lt;{{ getSenderEmail(message.received_from) }}&gt;
                  </span>
                </div>
                <div class="wm-message-recipients" v-if="message.type === 'SENT'">to {{ message.sent_to }}</div>
                <div class="wm-message-recipients" v-if="message.cc">cc {{ message.cc }}</div>
              </div>

              <div class="wm-message-side">
                <span class="wm-message-date">{{ getFormattedDateAndTime(message.received_at) }}</span>
                <div class="wm-message-actions">
                  <button class="wm-icon-btn wm-icon-btn-sm" type="button" title="Reply"
                          @click="triggerReply(message, $event)">
                    <i class="fas fa-reply"></i>
                  </button>
                  <button class="wm-icon-btn wm-icon-btn-sm" type="button" title="Reply All"
                          @click="triggerReplyToAll(message, $event)">
                    <i class="fas fa-reply-all"></i>
                  </button>
                  <button class="wm-icon-btn wm-icon-btn-sm" type="button" title="Forward"
                          @click="triggerForward(message, $event)">
                    <i class="fas fa-share"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="wm-message-body parsed-email"
                 v-html="message.message.replace(/(<style[\w\W]+style>)/g, '').replace(/class=/g, '')">
            </div>

            <div class="wm-attachments" v-if="getFileAttachments(message).length">
              <div class="wm-attachments-title">
                {{ getFileAttachments(message).length }}
                {{ getFileAttachments(message).length === 1 ? 'Attachment' : 'Attachments' }}
              </div>
              <div class="wm-attachments-list">
                <a v-for="attachment in getFileAttachments(message)"
                   :key="attachment.id"
                   target="_blank"
                   :href="attachment.uri"
                   class="wm-attachment"
                   :title="attachment.filename"
                   @click="loadAttachment(message, attachment, $event)">
                  <img class="wm-attachment-icon" :src="getAttachmentIcon(attachment.filename)" alt="">
                  <span class="wm-attachment-name">{{ attachment.filename }}</span>
                </a>
              </div>
            </div>
          </article>
        </template>

        <div v-else class="wm-reader-empty">
          <i class="far fa-envelope-open"></i>
          <p>Select a conversation to read</p>
        </div>
      </section>
    </div>

    <div class="new-mail-design">
      <!-- Gmail style compose windows docked at the bottom right: new message and reply/forward -->
      <div class="ic-compose-dock">
        <div v-show="isAnyComposeMaximized" class="ic-compose-backdrop" @click="exitComposeFullScreen"></div>

        <transition name="ic-compose-slide">
          <div v-show="new_compose.open" class="ic-compose-main-heads"
               :class="{'min': new_compose.minimized, 'max': new_compose.maximized}">
            <div class="ic-compose-headers" @click="restoreComposeWindow(new_compose)">
              <p>New Message</p>
              <ul>
                <li>
                  <a href="#" :title="new_compose.minimized ? 'Restore' : 'Minimize'"
                     @click.prevent.stop="toggleComposeMinimized(new_compose)">
                    <i class="fas" :class="new_compose.minimized ? 'fa-window-maximize' : 'fa-minus'"></i>
                  </a>
                </li>
                <li>
                  <a href="#" :title="new_compose.maximized ? 'Exit full screen' : 'Full screen'"
                     @click.prevent.stop="toggleComposeMaximized(new_compose)">
                    <i class="fas" :class="new_compose.maximized ? 'fa-compress-alt' : 'fa-expand-alt'"></i>
                  </a>
                </li>
                <li>
                  <a href="#" title="Close" @click.prevent.stop="closeComposePopup">
                    <i class="fas fa-times"></i>
                  </a>
                </li>
              </ul>
            </div>
            <div v-show="!new_compose.minimized" class="ic-compose-message-area">
              <div class="ic-inner-message-div">
                <div class="ic-main-message-heads">
                  <compose-webmail reply-to=""
                                   send-to=""
                                   reply-body=""
                                   forward-body=""
                                   popup
                                   @sent="closeComposePopup"></compose-webmail>
                </div>
                <div class="nk-reply-form-tools">
                  <button class="btn btn-primary" type="submit" @click="triggerSendEmail">Send</button>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <transition name="ic-compose-slide">
          <div v-if="active_thread && show_compose" class="ic-compose-main-heads"
               :class="{'min': reply_window.minimized, 'max': reply_window.maximized}">
            <div class="ic-compose-headers" @click="restoreComposeWindow(reply_window)">
              <p>{{ replyWindowTitle }}</p>
              <ul>
                <li>
                  <a href="#" :title="reply_window.minimized ? 'Restore' : 'Minimize'"
                     @click.prevent.stop="toggleComposeMinimized(reply_window)">
                    <i class="fas" :class="reply_window.minimized ? 'fa-window-maximize' : 'fa-minus'"></i>
                  </a>
                </li>
                <li>
                  <a href="#" :title="reply_window.maximized ? 'Exit full screen' : 'Full screen'"
                     @click.prevent.stop="toggleComposeMaximized(reply_window)">
                    <i class="fas" :class="reply_window.maximized ? 'fa-compress-alt' : 'fa-expand-alt'"></i>
                  </a>
                </li>
                <li>
                  <a href="#" title="Close" @click.prevent.stop="closeReplyWindow">
                    <i class="fas fa-times"></i>
                  </a>
                </li>
              </ul>
            </div>
            <div v-show="!reply_window.minimized" class="ic-compose-message-area">
              <div class="ic-inner-message-div">
                <div class="ic-main-message-heads">
                  <compose-webmail :reply-to="reply_to"
                                   :send-to="recipients"
                                   :reply-body="reply_body"
                                   :forward-body="forward_body"
                                   popup
                                   ref="reply_form"
                                   @sent="closeReplyWindow"></compose-webmail>
                </div>
                <div class="nk-reply-form-tools">
                  <button class="btn btn-primary" type="submit" @click="triggerSendEmail">Send</button>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from 'moment';
import {createTag} from "@johmun/vue-tags-input";

export default {
  data() {
    return {
      signature: '',
      footer: '',
      emails: [],
      threads: [],
      type: '',
      active_thread: null,
      active_thread_id: '',
      reply_to: '',
      forward_body: '',
      reply_body: '',
      loadMore: true,
      loading: false,
      take: 15,
      skip: 0,
      show_compose: false,
      compose_mode: '',
      recipients: [],
      selected_threads: [],
      account_id: '',
      unread_inbox_count: 0,
      syncing: 0,
      sync_percentage: 0,
      searching: false,
      search: '',
      emailAccounts: [],
      selectedEmailAccount: '',
      sidebar_collapsed: false,
      new_compose: {
        open: false,
        minimized: false,
        maximized: false,
      },
      reply_window: {
        minimized: false,
        maximized: false,
      },
      folders: [],
    };
  },

  computed: {
    replyWindowTitle() {
      let titles = {reply: 'Reply', reply_all: 'Reply all', forward: 'Forward'};
      let subject = this.getActiveThreadSubject();
      return (titles[this.compose_mode] || 'Reply') + (subject ? ': ' + subject : '');
    },

    isAnyComposeMaximized() {
      return (this.new_compose.open && this.new_compose.maximized)
          || (this.show_compose && this.reply_window.maximized);
    },

    allThreadsSelected() {
      return this.threads.length > 0 && this.threads.every(thread => this.selected_threads.includes(thread));
    },

    activeAccountEmail() {
      let account = this.emailAccounts.find(account => account.id === this.selectedEmailAccount);
      return account ? account.email : '';
    },

    syncProgress() {
      return {
        'width': this.sync_percentage + '%',
      }
    },

    /**
     * System folders first, then the rest of the tree alphabetically with children under their parent
     */
    sortedFolders() {
      let order = ['INBOX', 'SENT', 'TRASH'];
      let byName = (a, b) => (a.name || '').localeCompare(b.name || '');
      let ids = this.folders.map(folder => folder.folder_id);
      let sorted = [];

      let addChildren = (parent_id, depth) => {
        this.folders
            .filter(folder => folder.parent_folder_id === parent_id && !folder.well_known)
            .sort(byName)
            .forEach(folder => {
              sorted.push({...folder, depth: depth});
              addChildren(folder.folder_id, depth + 1);
            });
      };

      order.forEach(well_known => {
        this.folders.filter(folder => folder.well_known === well_known).forEach(folder => {
          sorted.push({...folder, depth: 0});
          addChildren(folder.folder_id, 1);
        });
      });

      // Top level user folders (their parent is the mailbox root, which is not in the list)
      this.folders
          .filter(folder => !folder.well_known && !ids.includes(folder.parent_folder_id))
          .sort(byName)
          .forEach(folder => {
            sorted.push({...folder, depth: 0});
            addChildren(folder.folder_id, 1);
          });

      return sorted;
    }
  },
  methods: {

    getFolders() {
      axios.get('/mail/folders')
          .then(res => {
            this.folders = res.data;
          });
    },

    getFolderType(folder) {
      let types = {INBOX: 'inbox', SENT: 'sent', TRASH: 'trash'};
      return types[folder.well_known] || 'folder-' + folder.id;
    },

    getFolderIcon(folder) {
      let icons = {INBOX: 'fas fa-inbox', SENT: 'far fa-paper-plane', TRASH: 'far fa-trash-alt'};
      return icons[folder.well_known] || 'far fa-folder';
    },

    getFolderUnread(folder) {
      return folder.well_known === 'INBOX' ? this.unread_inbox_count : folder.unread;
    },

    onSearchChange(e) {
      this.searching = this.search.length;
    },

    triggerSearch(e) {
      if (!this.search.length) {
        return;
      }
      this.skip = 0;
      this.loadMore = true;
      this.threads = [];
      this.emails = [];
      this.skip = 0;
      this.searchEmails();
    },

    clearSearch(e) {
      this.searching = false;
      this.search = '';
      this.skip = 0;
      this.loadMore = true;
      this.getEmails(this.type, true);
    },

    searchEmails(clear = false) {
      if (this.search.length) {
        this.loading = true;
        axios.get(`/mail/search/${this.type}/${this.search}`, {
          params: {
            take: this.take,
            skip: this.skip
          }
        })
            .then(res => {
              this.loading = false;
              let emails = res.data;
              let threads = Object.keys(emails);
              this.threads = this.threads.concat(threads);
              this.emails = {...this.emails, ...emails};
              this.skip = this.threads.length;

              if (!threads.length) {
                this.loadMore = false;
              }

              if (this.active_thread_id) {
                this.active_thread = this.emails[this.active_thread_id];
              } else {
                let first_thread = this.threads[0];
                this.active_thread = this.emails[first_thread];
                this.active_thread_id = first_thread;
              }
            });
      }
    },

    triggerSendEmail(e) {
      e.preventDefault();
      // Only send the form that belongs to the clicked Send button (reply modal or new message window)
      $(e.target).closest('.modal-content, .ic-compose-main-heads').find('.send-email-btn').trigger('click');
    },

    loadAttachment(message, attachment, event) {
      event.preventDefault();
      this.active_thread.forEach((thread_message, index) => {
        if (thread_message.id === message.id) {
          let attachments = this.active_thread[index].attachments;

          attachments.forEach((message_attachment, attachment_index) => {
            if (message_attachment.id === attachment.id) {
              let url = this.active_thread[index].attachments[attachment_index].uri;

              if (url) {
                window.open(url, '_blank').focus();
              } else {
                let loading = this.active_thread[index].attachments[attachment_index].loading;
                if (!loading) {

                  this.active_thread[index].attachments[attachment_index].loading = true;

                  $.ajax({
                    type: 'GET',
                    url: '/mail/attachment/load',
                    data: {
                      attachment: attachment.id
                    },
                    success: (url) => {
                      this.active_thread[index].attachments[attachment_index].uri = url;
                      this.active_thread[index].attachments[attachment_index].loading = false;
                      window.open(url, '_blank').focus();
                    }
                  });
                }
              }
            }
          });
        }
      });
    },

    getAttachmentIcon(uri) {
      let ext = uri.split(/[#?]/)[0].split('.').pop().trim();
      let images_extensions = ['png', 'jpeg', 'jpg', 'gif'],
          video_extensions = ['webm', 'mkv', 'flv', 'ogg', 'wmv', 'm4v'],
          word_extensions = ['doc', 'docm', 'docx', 'dot', 'dotx', 'odt'],
          powerpoint_extensions = ['pptx', 'pptm', 'ppt'];

      if (images_extensions.includes(ext)) {
        return '/images/webmail/attachments/photo.png';
      }

      if (video_extensions.includes(ext)) {
        return '/images/webmail/attachments/video.jpg';
      }

      if (word_extensions.includes(ext)) {
        return '/images/webmail/attachments/word.png';
      }

      if (powerpoint_extensions.includes(ext)) {
        return '/images/webmail/attachments/ppt.png';
      }

      if (ext === 'tiff') {
        return '/images/webmail/attachments/tiff.png';
      }

      if (ext === 'pdf') {
        return '/images/webmail/attachments/pdf.png';
      }

      return '/images/webmail/attachments/file.png';

    },

    selectAllThreads(e) {
      let checker = (arr, target) => target.every(v => arr.includes(v));
      let already_checked = checker(this.selected_threads, this.threads);
      if (already_checked) {
        this.selected_threads = [];
      } else {
        this.selected_threads = this.threads.slice();
      }
    },

    triggerReply(message, e) {
      e.preventDefault();
      let signature = '<br><div>' + this.signature + '</div>';
      this.openReplyWindow('reply');
      this.reply_to = message.message_id;
      this.reply_body = signature + this.getQuotedMessage(message);
      this.$nextTick(() => {
        this.$refs.reply_form.updateRecipients(this.getSender());
        this.$refs.reply_form.updateCCs(this.getCC());
        this.$refs.reply_form.setupReplyEmail(this.reply_body);
        this.$refs.reply_form.setReplyTo(message.message_id);
      });
      // $('.active-email').animate({scrollTop: $('.active-email')[0].scrollHeight}, "slow");
    },

    triggerReplyToAll(message, e) {
      e.preventDefault();
      let signature = '<br><div>' + this.signature + '</div>';
      this.openReplyWindow('reply_all');
      this.reply_to = message.message_id;
      this.reply_body = signature + this.getQuotedMessage(message);
      this.$nextTick(() => {
        this.$refs.reply_form.updateRecipients(this.getSender());
        this.$refs.reply_form.updateCCs(this.getCC());
        this.$refs.reply_form.setupReplyEmail(this.reply_body);
        this.$refs.reply_form.setReplyTo(message.message_id);
      });
      // $('.active-email').animate({scrollTop: $('.active-email')[0].scrollHeight}, "slow");
    },

    triggerForward(message, e) {
      let body = '';
      body = '<br>' + this.signature;
      body += '---------- Forwarded message ---------<br>';
      body += 'From: ' + message.received_from + '<br>';
      body += 'Date: ' + message.received_at + '<br>';
      body += 'Subject: ' + message.subject + '<br>';
      body += 'To: ' + message.sent_to + '<br><br>';
      body += '<div>' + this.stripMessageStyles(message.message) + '</div>';

      this.openReplyWindow('forward');
      this.reply_to = '';
      this.forward_body = body;
      this.$nextTick(() => {
        this.$refs.reply_form.updateRecipients([]);
        this.$refs.reply_form.setupForwardEmail(body);
        this.$refs.reply_form.setReplyTo('');
        this.$refs.reply_form.setSubject('Fwd: ' + this.getThreadSubject(message.thread_id));
      });
      // $('.active-email').animate({scrollTop: $('.active-email')[0].scrollHeight}, "slow");
    },

    // Original message quoted below a reply, Gmail style ("On <date>, <sender> wrote:")
    getQuotedMessage(message) {
      let date = this.getFormattedDateAndTime(message.received_at);
      let sender = $('<div>').text(message.received_from || message.sent_to || '').html();
      return '<br><div class="gmail_quote">'
          + '<div>On ' + date + ', ' + sender + ' wrote:</div>'
          + '<blockquote style="margin: 0 0 0 0.8ex; border-left: 1px solid #ccc; padding-left: 1ex;">'
          + this.stripMessageStyles(message.message)
          + '</blockquote></div>';
    },

    stripMessageStyles(html) {
      return (html || '').replace(/(<style[\w\W]+style>)/g, '');
    },

    getEmails(type, clear = false) {
      this.loading = true;
      axios.get(`/mail/emails/${type}`, {
        params: {
          take: this.take,
          skip: this.skip
        }
      })
          .then(res => {
            this.loading = false;
            let emails = res.data;
            let threads = Object.keys(emails);
            if (clear) {
              this.threads = [];
              this.emails = [];
              this.skip = 0;
            }
            this.threads = this.threads.concat(threads);
            this.emails = {...this.emails, ...emails};
            this.skip = this.threads.length;

            if (!threads.length) {
              this.loadMore = false;
            }

            if (this.active_thread_id) {
              this.active_thread = this.emails[this.active_thread_id];
            } else {
              let first_thread = this.threads[0];
              this.active_thread = this.emails[first_thread];
              this.active_thread_id = first_thread;
            }
          });
    },

    handleMessageScroll() {
      let $container = $('.incoming-emails');
      $container.on('scroll', (e) => {
        let element = event.target;
        let scrolled_height = element.scrollHeight - element.scrollTop - 5;
        if (scrolled_height <= element.clientHeight) {
          if (this.loadMore && !this.loading && !this.searching) {
            this.getEmails(this.type);
          } else if (this.loadMore && !this.loading && this.searching) {
            this.searchEmails();
          }
        }
      });
    },

    getFormattedDateAndTime(date) {
      return moment(date).format('M/D/Y hh:mm a');
    },

    getFormattedDate(thread_id) {
      let emails = this.emails[thread_id];
      let count = emails.length;
      let last_email = emails[0];
      let date = moment.utc(last_email.received_at);
      if (count > 1) {
        for (const el of emails) {
          if (el.type.toLowerCase() === this.type.toLowerCase()) {
            date = moment.utc(el.received_at);
            break;
          }
        }
      }
      return date.isValid() ? date.format('ddd M/D') : '';
    },

    getThreadMessagesCount(thread) {
      return this.emails[thread].length > 1 ? this.emails[thread].length : "";
    },

    getThreadUser(thread_id) {
      let emails = this.emails[thread_id];
      let count = emails.length;
      let last_email = emails[count - 1],
          first_email = emails[0];

      let user = '';

      if (first_email.type === 'INBOX') {
        if (first_email.received_from.length) {
          user = first_email.received_from;
        }
      } else if (first_email.type === 'SENT') {
        if (first_email.sent_to.length) {
          user = first_email.sent_to;
        }
      } else if (first_email.type === '') {
        user = first_email.received_from || first_email.sent_to || '';
      } else if (first_email.type === 'TRASH') {
        let labels = first_email.labels.length ? JSON.parse(first_email.labels) : [first_email.type];

        if (labels.includes('INBOX')) {
          if (first_email.received_from.length) {
            user = first_email.received_from;
          }
        } else if (labels.includes('TRASH')) {
          if (first_email.sent_to.length) {
            user = first_email.sent_to;
          }
        }
      }
      if (user.length) {
        if (user[0] === '<') {
          user = user.split('<').pop().split('>')[0];
        }
        return user;
      }
      return '(No User)';
    },

    getMessageUser(message) {
      let user = '';

      if (message.type === 'INBOX') {
        if (message.received_from.length) {
          user = message.received_from;
        }
      } else if (message.type === 'SENT') {
        if (message.sent_to.length) {
          user = message.sent_to;
        }
      } else if (message.type === '') {
        user = message.received_from || message.sent_to || '';
      } else if (message.type === 'TRASH') {
        let labels = message.labels.length ? JSON.parse(message.labels) : [message.type];

        if (labels.includes('INBOX')) {
          if (message.received_from.length) {
            user = message.received_from;
          }
        } else if (labels.includes('TRASH')) {
          if (message.sent_to.length) {
            user = message.sent_to;
          }
        }
      }
      if (user.length) {
        if (user[0] === '<') {
          user = user.split('<').pop().split('>')[0];
        }
        return user;
      }
      return '(No User)';
    },

    getThreadSubject(thread_id) {
      let emails = this.emails[thread_id];
      let count = emails.length;
      let last_email = emails[count - 1];

      if (last_email.subject && last_email.subject.length) {
        return last_email.subject;
      }
      return '(No Subject)';
    },

    getActiveThreadSubject() {
      if (this.active_thread) {
        let count = this.active_thread.length;
        let first_email = this.active_thread[0],
            last_email = this.active_thread[count - 1];
        if (last_email.subject.length) {
          return last_email.subject;
        }
        return '(No Subject)';
      }
      return '';
    },

    getSender() {
      if (this.active_thread) {
        if (this.compose_mode === 'reply') {
          let count = this.active_thread.length;
          let first_email = this.active_thread[0],
              last_email = this.active_thread[count - 1];
          if (last_email.received_from.length) {
            let received_from = last_email.received_from;
            return [received_from.split('<').pop().split('>')[0]]; // returns 'two'
          }
        } else if (this.compose_mode === 'reply_all') {
          let emails = [];
          this.active_thread.forEach(message => {
            if (message.received_from.length) {
              let received_from = message.received_from;
              emails.push(received_from.split('<').pop().split('>')[0]);
            }
          });
          return emails;
        } else if (this.compose_mode === 'forward') {
          return [];
        }
      }
    },

    getCC() {
      if (this.active_thread) {
        if (this.compose_mode === 'reply') {
          let count = this.active_thread.length;
          let first_email = this.active_thread[0],
              last_email = this.active_thread[count - 1];
          if (last_email.cc && last_email.cc.length) {
            let received_from = last_email.cc;
            return [received_from.split('<').pop().split('>')[0]]; // returns 'two'
          }
        } else if (this.compose_mode === 'reply_all') {
          let emails = [];
          this.active_thread.forEach(message => {
            if (message.cc.length) {
              let received_from = message.cc;
              emails.push(received_from.split('<').pop().split('>')[0]);
            }
          });
          return emails;
        } else if (this.compose_mode === 'forward') {
          return [];
        }
      }
      return [];
    },

    // Who to show as the sender of a message: the other party for received mail, the active account for sent mail
    getMessageSender(message) {
      if (message.type === 'SENT') {
        return this.activeAccountEmail || 'Me';
      }
      return this.getMessageUser(message);
    },

    getDisplayName(user) {
      let name = (user || '').split('<')[0].replace(/["']/g, '').trim();
      return name.length ? name : this.getSenderEmail(user || '');
    },

    getInitial(user) {
      let name = this.getDisplayName(user);
      let match = name.match(/[A-Za-z0-9\u00C0-\u024F]/);
      return match ? match[0].toUpperCase() : '?';
    },

    getAvatarColor(user) {
      let colors = ['#1a73e8', '#d93025', '#188038', '#e37400', '#9334e6', '#c5221f', '#129eaf', '#b06000'];
      let name = this.getDisplayName(user);
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) % 1000;
      }
      return colors[hash % colors.length];
    },

    getFileAttachments(message) {
      return (message.attachments || []).filter(attachment => attachment.type === 'attachment');
    },

    getSenderEmail(receiver) {
      return receiver.split('<').pop().split('>')[0];
    },

    getThreadSnippet(thread_id) {
      let emails = this.emails[thread_id];
      let first_email = emails[0];

      if (first_email.snippet.length) {
        return first_email.snippet;
      }
      return '';
    },

    getUnreadCount(type, clear = false) {
      axios.get(`/mail/emails/unread_count`)
          .then(res => {
            this.unread_inbox_count = res.data;
          });
    },

    markThreadAsRead(thread_id) {
      axios.get(`/mail/emails/mark_thread_as_read/${thread_id}`, {
        params: {
          account_id: this.account_id,
        }
      })
          .then(res => {
            this.getUnreadCount();

            let emails = this.emails[thread_id];
            emails.forEach((email, index) => {
              this.emails[thread_id][index].is_read = 1;
            });
          });
    },

    markThreadAsUnRead(thread_id) {
      axios.get(`/mail/emails/mark_thread_as_unread/${thread_id}`, {
        params: {
          account_id: this.account_id,
        }
      })
          .then(res => {
            this.getUnreadCount();

            let emails = this.emails[thread_id];
            emails.forEach((email, index) => {
              this.emails[thread_id][index].is_read = 0;
            });
          });
    },

    isThreadMarked(thread) {
      return this.selected_threads.includes(thread);
    },

    isThreadRead(thread) {
      let emails = this.emails[thread];
      let is_read = true;
      emails.forEach((email, index) => {
        if (!email.is_read) {
          is_read = false;
        }
      });
      return is_read;
    },

    deleteThreads() {
      axios.delete(`/mail/emails`, {
        params: {
          type: this.type,
          threads: this.selected_threads,
          account_id: this.account_id,
        }
      })
          .then(res => {
            if (res.data) {

              this.selected_threads.forEach((selected_thread, index) => {
                this.threads = this.threads.filter(function (thread, index, arr) {
                  return thread !== selected_thread;
                });
              });

              if (this.selected_threads.includes(this.active_thread_id)) {
                this.active_thread = null;
                this.active_thread_id = '';
              }

              this.selected_threads = [];

            }
          });
    },

    toggleThread(thread, e) {
      if (this.selected_threads.includes(thread)) {
        this.selected_threads = this.selected_threads.filter(function (selected_thread, index, arr) {
          return thread !== selected_thread;
        });
      } else {
        this.selected_threads.push(thread);
      }
    },

    toggleThreadRead(thread, e) {
      e.preventDefault();
      e.stopPropagation();
      if (this.isThreadRead(thread)) {
        this.markThreadAsUnRead(thread);
      } else {
        this.markThreadAsRead(thread);
      }
    },
    openComposePopup() {
      this.new_compose.open = true;
      this.new_compose.minimized = false;
    },

    closeComposePopup() {
      this.new_compose.open = false;
      this.new_compose.minimized = false;
      this.new_compose.maximized = false;
    },

    openReplyWindow(mode) {
      this.compose_mode = mode;
      this.show_compose = true;
      this.reply_window.minimized = false;
    },

    closeReplyWindow() {
      this.show_compose = false;
      this.compose_mode = '';
      this.reply_window.minimized = false;
      this.reply_window.maximized = false;
    },

    toggleComposeMinimized(win) {
      win.minimized = !win.minimized;
      if (win.minimized) {
        win.maximized = false;
      }
    },

    toggleComposeMaximized(win) {
      let maximize = !win.maximized;
      // Only one window can be full screen at a time
      this.exitComposeFullScreen();
      win.maximized = maximize;
      win.minimized = false;
    },

    restoreComposeWindow(win) {
      if (win.minimized) {
        win.minimized = false;
      }
    },

    exitComposeFullScreen() {
      this.new_compose.maximized = false;
      this.reply_window.maximized = false;
    },

    updateActiveAccount() {
      // Assuming you have an API endpoint to update the active account
      const url = `/user/mail_boxes/active`;

      // Make the HTTP request (using axios or fetch)
      axios.post(url, {
        id: this.selectedEmailAccount
      })
      .then(response => {
        window.location.reload();
      })
      .catch(error => {
        // Handle error
        console.error('Error updating active account:', error);
      });
    }
  },
  mounted: function () {
    let $mount = $('#webmail');
    this.account_id = $mount.attr('data-account-id');
    this.syncing = parseInt($mount.attr('data-account-syncing'));
    this.sync_percentage = parseInt($mount.attr('data-account-sync-percentage'));
    if (this.$route.params.thread !== undefined) {
      this.active_thread_id = this.$route.params.thread;
    }
    this.type = this.$route.params.type;
    this.getEmails(this.$route.params.type);
    this.getUnreadCount();
    this.getFolders();
    this.handleMessageScroll();

    axios.get('/settings/users/signatures')
        .then(res => {
          this.footer = res.data.footer;
          this.signature = res.data.email;
        }).catch((err) => {
      // console.log(err);
    });

    // Axios GET request to fetch email accounts
    axios.get('/user/mail_boxes')
      .then(response => {
        this.emailAccounts = response.data.accounts;
        this.selectedEmailAccount = response.data.active_account;
      })
      .catch(error => {
        console.error('Error fetching email accounts:', error);
      });

  },
  watch: {
    $route(to, from) {
      this.active_thread = null;
      this.active_thread_id = '';
      this.reply_to = '';
      this.reply_body = '';
      this.forward_body = '';

      if (to.params.thread !== undefined) {
        this.active_thread_id = to.params.thread;
        this.markThreadAsRead(this.active_thread_id);
      }

      /**
       * If type of the inbox is changed, then update/reset the skip count and loadMore attributes
       */
      if (to.params.type !== from.params.type) {
        this.type = to.params.type;
        this.skip = 0;
        this.loadMore = true;
        this.getEmails(to.params.type, true);
      } else {

        this.active_thread_id = to.params.thread;
        this.closeReplyWindow();

        if (this.active_thread_id) {
          this.active_thread = this.emails[this.active_thread_id];
        } else {
          let first_thread = this.threads[0];
          this.active_thread = this.emails[first_thread];
          this.active_thread_id = first_thread;
        }
      }
    }
  }
}
</script>
